import type { ChronologyEventLight } from '@/hooks/useChronology';
import type { RegionalDateLocale } from '@/utils/date-utils';

export const DAY_MS = 86_400_000;
const MONTH_MS = 30.4375 * DAY_MS;
const YEAR_MS = 365.25 * DAY_MS;

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export interface TimeWindow {
  start: number;
  end: number;
}

export interface TickSpec {
  unit: 'day' | 'month' | 'year';
  step: number;
}

const TICK_SPECS: TickSpec[] = [
  { unit: 'day', step: 1 },
  { unit: 'day', step: 7 },
  { unit: 'month', step: 1 },
  { unit: 'month', step: 3 },
  { unit: 'year', step: 1 },
  { unit: 'year', step: 2 },
  { unit: 'year', step: 5 },
  { unit: 'year', step: 10 },
  { unit: 'year', step: 25 },
  { unit: 'year', step: 50 },
  { unit: 'year', step: 100 },
];

function approxSpecMs(spec: TickSpec): number {
  const unitMs = spec.unit === 'day' ? DAY_MS : spec.unit === 'month' ? MONTH_MS : YEAR_MS;
  return unitMs * spec.step;
}

/** The finest tick interval whose ticks land at least `minSpacingPx` apart. */
export function chooseTickSpec(msPerPx: number, minSpacingPx: number): TickSpec {
  return TICK_SPECS.find(spec => approxSpecMs(spec) / msPerPx >= minSpacingPx) ?? TICK_SPECS[TICK_SPECS.length - 1];
}

/** The coarser band drawn under the minor ticks (months under days, years under months). */
export function majorSpecFor(minor: TickSpec): TickSpec | null {
  if (minor.unit === 'day') return { unit: 'month', step: 1 };
  if (minor.unit === 'month') return { unit: 'year', step: 1 };
  return null;
}

function floorToSpec(t: number, spec: TickSpec): number {
  const d = new Date(t);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  if (spec.unit === 'year') return Date.UTC(Math.floor(year / spec.step) * spec.step, 0, 1);
  if (spec.unit === 'month') return Date.UTC(year, Math.floor(month / spec.step) * spec.step, 1);
  const day = Date.UTC(year, month, d.getUTCDate());
  if (spec.step === 7) {
    const daysSinceMonday = (new Date(day).getUTCDay() + 6) % 7;
    return day - daysSinceMonday * DAY_MS;
  }
  return day;
}

function addSpec(t: number, spec: TickSpec): number {
  if (spec.unit === 'day') return t + spec.step * DAY_MS;
  const d = new Date(t);
  if (spec.unit === 'month') return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + spec.step, 1);
  return Date.UTC(d.getUTCFullYear() + spec.step, 0, 1);
}

/** Tick instants inside the window; `includeLeading` adds the boundary just before it. */
export function generateTicks(window: TimeWindow, spec: TickSpec, includeLeading = false): number[] {
  const ticks: number[] = [];
  let t = floorToSpec(window.start, spec);
  for (let guard = 0; t <= window.end && guard < 2000; guard++) {
    if (t >= window.start || (includeLeading && ticks.length === 0)) ticks.push(t);
    t = addSpec(t, spec);
  }
  return ticks;
}

export function formatTickLabel(t: number, spec: TickSpec): string {
  const d = new Date(t);
  if (spec.unit === 'day') return String(d.getUTCDate());
  if (spec.unit === 'month') return MONTHS_SHORT[d.getUTCMonth()];
  return String(d.getUTCFullYear());
}

export function formatMajorLabel(t: number, spec: TickSpec): string {
  const d = new Date(t);
  if (spec.unit === 'month') return `${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  return String(d.getUTCFullYear());
}

/** Human label for the visible window, at a precision that suits its span. */
export function formatWindowLabel(window: TimeWindow, locale: RegionalDateLocale): string {
  const a = new Date(window.start);
  const b = new Date(window.end);
  const span = window.end - window.start;
  const month = (d: Date) => MONTHS_SHORT[d.getUTCMonth()];
  if (span < 90 * DAY_MS) {
    const day = (d: Date, withYear: boolean) => {
      const base = locale === 'us' ? `${month(d)} ${d.getUTCDate()}` : `${d.getUTCDate()} ${month(d)}`;
      return withYear ? (locale === 'us' ? `${base}, ${d.getUTCFullYear()}` : `${base} ${d.getUTCFullYear()}`) : base;
    };
    const sameYear = a.getUTCFullYear() === b.getUTCFullYear();
    return `${day(a, !sameYear)} – ${day(b, true)}`;
  }
  if (span < 4 * YEAR_MS) return `${month(a)} ${a.getUTCFullYear()} – ${month(b)} ${b.getUTCFullYear()}`;
  return `${a.getUTCFullYear()} – ${b.getUTCFullYear()}`;
}

export type TimelineItemKind = 'point' | 'approximate' | 'range';

export interface TimelineItem {
  event: ChronologyEventLight;
  start: number;
  /** Exclusive end for spans; null for single instants. */
  end: number | null;
  kind: TimelineItemKind;
}

/**
 * Where an event sits in time. A month- or year-precision date is an
 * approximate point (its span is uncertainty, not duration); anything else
 * longer than a day is a real range.
 */
export function toTimelineItem(event: ChronologyEventLight): TimelineItem | null {
  if (!event.temporal_lower) return null;
  const start = event.temporal_lower.getTime();
  const upper = !event.temporal_upper_inf && event.temporal_upper ? event.temporal_upper.getTime() : null;
  if (upper === null || upper - start <= DAY_MS) return { event, start, end: null, kind: 'point' };
  const duration = upper - start;
  const samePrecision = event.start_precision === event.end_precision;
  if (samePrecision && event.start_precision === 'month' && duration <= 31 * DAY_MS) {
    return { event, start, end: upper, kind: 'approximate' };
  }
  if (samePrecision && event.start_precision === 'year' && duration <= 366 * DAY_MS) {
    return { event, start, end: upper, kind: 'approximate' };
  }
  return { event, start, end: upper, kind: 'range' };
}

/** Padded bounds covering every item, so nothing sits flush against an edge. */
export function timelineExtent(items: readonly TimelineItem[]): TimeWindow | null {
  if (items.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const item of items) {
    min = Math.min(min, item.start);
    max = Math.max(max, item.end ?? item.start);
  }
  const pad = Math.max((max - min) * 0.04, 20 * DAY_MS);
  return { start: min - pad, end: max + pad };
}

/** Tightest scale for the opening view: one card covers about a fortnight. */
const OPENING_CARD_SPAN_MS = 14 * DAY_MS;
/** Furthest the opening view zooms out while reaching for later events. */
const OPENING_MAX_CARD_SPAN_MS = 60 * DAY_MS;

/**
 * The view shown on open. Starts tight on the first events so a busy period
 * stays readable. When that slice holds only a few events, it widens — up to
 * a few months per card — until a short run of them is in view. Anything that
 * fits inside the chosen window is shown in full.
 */
export function openingWindow(starts: readonly number[], fitted: TimeWindow, widthPx: number, cardPx: number): TimeWindow {
  const first = starts[0];
  if (first == null || widthPx <= 0 || cardPx <= 0) return fitted;

  const cardsAcross = Math.max(1, widthPx / cardPx);
  const baseSpan = cardsAcross * OPENING_CARD_SPAN_MS;
  const maxSpan = cardsAcross * OPENING_MAX_CARD_SPAN_MS;
  const fittedSpan = fitted.end - fitted.start;
  if (baseSpan >= fittedSpan) return fitted;

  const targetCount = Math.min(8, Math.max(4, Math.round(cardsAcross) + 2));
  let coverage = baseSpan;
  let count = 0;
  for (const start of starts) {
    if (start > first + baseSpan) break;
    count += 1;
  }
  if (count < targetCount) {
    const target = starts[Math.min(targetCount, starts.length) - 1];
    const cardFraction = Math.min(0.4, cardPx / widthPx);
    const needed = (target - first) / (1 - cardFraction);
    coverage = Math.min(maxSpan, Math.max(baseSpan, needed));
  }
  if (coverage >= fittedSpan) return fitted;

  const inset = Math.min(coverage * 0.08, 10 * DAY_MS);
  return { start: first - inset, end: first + coverage };
}

export function zoomWindow(window: TimeWindow, factor: number, anchorRatio = 0.5): TimeWindow {
  const span = window.end - window.start;
  const anchor = window.start + span * anchorRatio;
  const nextSpan = span * factor;
  return { start: anchor - nextSpan * anchorRatio, end: anchor + nextSpan * (1 - anchorRatio) };
}

/**
 * Keep the same time-per-pixel when the canvas width changes, so cards stay
 * in their lanes. `anchor` is the rendered card's left edge, in pixels, at
 * `previousWidth`. The window shifts only when that edge was on screen and
 * the narrower canvas would leave the card past `maxX`.
 */
export function windowForCanvasResize(
  window: TimeWindow,
  previousWidth: number,
  nextWidth: number,
  anchor: { x: number; maxX: number } | null,
): TimeWindow {
  if (previousWidth <= 0 || nextWidth <= 0 || previousWidth === nextWidth) return window;
  const msPerPx = (window.end - window.start) / previousWidth;
  const span = msPerPx * nextWidth;
  let start = window.start;
  if (anchor && msPerPx > 0 && anchor.x < previousWidth && anchor.x > anchor.maxX) {
    start += (anchor.x - anchor.maxX) * msPerPx;
  }
  return { start, end: start + span };
}

/**
 * A tighter window centred on a cluster of hidden events, or null when zooming
 * would not move the view. Same timestamps cannot be separated by zooming.
 */
export function clusterZoomTarget(
  starts: readonly number[],
  view: TimeWindow,
  width: number,
  options: { minSpan: number; cardPitchPx: number },
): TimeWindow | null {
  if (starts.length === 0 || width <= 0) return null;
  const currentSpan = view.end - view.start;
  if (currentSpan <= options.minSpan * 1.05) return null;
  let first = starts[0];
  let last = starts[0];
  const distinct = new Set<number>();
  for (const start of starts) {
    distinct.add(start);
    if (start < first) first = start;
    if (start > last) last = start;
  }
  if (distinct.size < 2) return null;
  const fitSpan = ((last - first) * width) / (distinct.size * options.cardPitchPx) * 1.25;
  const span = Math.min(Math.max(fitSpan, options.minSpan), currentSpan / 1.35);
  if (!(span < currentSpan * 0.92)) return null;
  const centre = (first + last) / 2;
  return { start: centre - span / 2, end: centre + span / 2 };
}

/** Keeps the span within zoom limits and the centre within the data. */
export function clampWindow(window: TimeWindow, extent: TimeWindow, minSpan: number, maxSpan: number): TimeWindow {
  const span = Math.min(Math.max(window.end - window.start, minSpan), maxSpan);
  const centre = Math.min(Math.max((window.start + window.end) / 2, extent.start), extent.end);
  return { start: centre - span / 2, end: centre + span / 2 };
}

export interface PackInput {
  id: string;
  start: number;
  end: number;
  /** Lower values claim lanes first. */
  priority: number;
}

export interface PackResult {
  lanes: Map<string, number>;
  overflow: Set<string>;
}

/** Insertion index in a lane sorted by start, or null when the card overlaps a neighbour. */
function laneInsertion(intervals: Array<[number, number]>, start: number, end: number): number | null {
  let lo = 0;
  let hi = intervals.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (intervals[mid][0] < start) lo = mid + 1;
    else hi = mid;
  }
  const before = intervals[lo - 1];
  const after = intervals[lo];
  if ((before && before[1] > start) || (after && after[0] < end)) return null;
  return lo;
}

/**
 * Assigns each card to the lowest free lane. Packing happens in time units,
 * so panning at a fixed zoom never reshuffles the lanes.
 */
export function packLanes(items: readonly PackInput[], laneCount: number): PackResult {
  const lanes = new Map<string, number>();
  const overflow = new Set<string>();
  const occupied: Array<Array<[number, number]>> = Array.from({ length: laneCount }, () => []);
  const order = [...items].sort((a, b) => a.priority - b.priority || a.start - b.start);
  for (const item of order) {
    let placed = false;
    for (let lane = 0; lane < laneCount; lane++) {
      const intervals = occupied[lane];
      const index = laneInsertion(intervals, item.start, item.end);
      if (index === null) continue;
      intervals.splice(index, 0, [item.start, item.end]);
      lanes.set(item.id, lane);
      placed = true;
      break;
    }
    if (!placed) overflow.add(item.id);
  }
  return { lanes, overflow };
}

export interface OverflowBucket {
  start: number;
  end: number;
  count: number;
}

function lowerBound(sorted: readonly number[], value: number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Counts hidden events per tick interval overlapping the window, so each axis interval gets at most one marker. */
export function bucketOverflow(times: readonly number[], window: TimeWindow, spec: TickSpec): OverflowBucket[] {
  const sorted = [...times].sort((a, b) => a - b);
  const buckets: OverflowBucket[] = [];
  let start = floorToSpec(window.start, spec);
  for (let guard = 0; start < window.end && guard < 2000; guard++) {
    const end = addSpec(start, spec);
    const count = lowerBound(sorted, end) - lowerBound(sorted, start);
    if (count > 0) buckets.push({ start, end, count });
    start = end;
  }
  return buckets;
}

/** Event counts per equal-width bin across the extent, for the overview strip. */
export function densityBins(items: readonly TimelineItem[], extent: TimeWindow, binCount: number): number[] {
  const bins = new Array<number>(Math.max(binCount, 1)).fill(0);
  const span = extent.end - extent.start;
  if (span <= 0) return bins;
  for (const item of items) {
    const index = Math.floor(((item.start - extent.start) / span) * bins.length);
    if (index >= 0 && index < bins.length) bins[index] += 1;
  }
  return bins;
}
